import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// The arctic night before the display, and the same sky and ice washed with
// green airglow once the aurora is at its height.
const STOPS = {
  skyTop: ['#02051A', '#030A24'],
  skyHigh: ['#061535', '#062440'],
  skyLow: ['#0C3349', '#0C4651'],
  horizon: ['#185A6A', '#1E7768'],
  lakeTop: ['#0B3346', '#0F4D4C'],
  lakeBottom: ['#030A18', '#04131F'],
} as const;

type ColorName = keyof typeof STOPS;

export type AuroraColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** Sky and lake colours for how bright the display is (0 quiet, 1 at its height). */
export const auroraColors = (glow: number): AuroraColors => {
  const amount = smoothstep(0, 1, clamp(glow));
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][0], PARSED[name][1], amount) }),
    {} as AuroraColors,
  );
};

/** The polar night stays deep at the top of the screen, so header text is always white. */
export const auroraChromeTone = (progress: number): ChromeTone =>
  luminance(auroraColors(progress).skyTop) > 0.31 ? 'dark' : 'light';
